import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { Link } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { TextEditor } from "@src/components/ui-kit/TextEditor/TextEditor";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import type { FC } from "react";

export const SendInvoiceModal: FC = observer(function SendInvoiceModal() {
  const { sendInvoiceModalStore: store } = useStore();

  return (
    <Modal
      isOpen
      onClose={store.modalState.onClose}
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent
        minW={{
          base: "full",
          md: "500px",
        }}
      >
        <ModalHeader>
          <Trans>Send mail</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {store.fetching ? (
            <Center h="300px">
              <Spinner color="teal.500" size="lg" />
            </Center>
          ) : (
            <VStack align="flex-start" spacing="4">
              <FormControl isInvalid={store.form.$.recipients.hasError}>
                <FormLabel>
                  <Trans>Recipients</Trans>
                </FormLabel>
                <Textarea
                  resize="vertical"
                  onChange={({ target }) => {
                    store.form.$.recipients.onChange(target.value);
                  }}
                  value={store.form.$.recipients.value}
                />
                <HStack align="center" mt="2">
                  <Icon name="info-circle" />
                  <FormHelperText>
                    <Trans>
                      If you use multiple emails, please separate them with ", "
                    </Trans>
                  </FormHelperText>
                </HStack>
                <FormErrorMessage>
                  {store.form.$.recipients.error}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={store.form.$.subject.hasError}>
                <FormLabel>
                  <Trans>Subject</Trans>
                </FormLabel>
                <Input
                  onChange={({ target }) => {
                    store.form.$.subject.onChange(target.value);
                  }}
                  value={store.form.$.subject.value}
                />
                <FormErrorMessage>
                  {store.form.$.subject.error}
                </FormErrorMessage>
              </FormControl>
              <FormControl>
                <FormLabel>
                  <Trans>Content</Trans>
                </FormLabel>
                <TextEditor
                  attachments={[]}
                  isEditable
                  initialValue={store.form.$.content.value}
                  onChange={(val) => {
                    store.form.$.content.onChange(val?.toString() ?? "");
                  }}
                  borderWidth="thin"
                  px="3"
                  borderColor="grey.200"
                  borderRadius="base"
                  transition="border-color 150ms ease-in"
                  _focusWithin={{
                    borderColor: "purple.500",
                  }}
                />
              </FormControl>
              <FormLabel>
                <Trans>Attachments</Trans>
              </FormLabel>
              {store.attachments.map((attachment) => (
                <WrapComponent
                  key={`attachment-${attachment.id}`}
                  if={!attachment.required}
                  with={(children) => (
                    <HStack align="center" spacing="2">
                      {children}
                      <IconButton
                        aria-label={t`Delete attachment`}
                        colorScheme="red"
                        icon={<Icon name="trash-03" />}
                        onClick={() =>
                          store.handleDeleteAttachment(attachment.id)
                        }
                        size="sm"
                        variant="ghost"
                      />
                    </HStack>
                  )}
                >
                  <Link href={attachment.link} isExternal>
                    {attachment.name}
                  </Link>
                </WrapComponent>
              ))}
              <HStack align="start" textColor="gray.600" fontSize="sm">
                <Icon name="info-circle" mt="1" />
                <div>
                  <Trans>
                    The attachment name can be configured in Workspace Settings
                    under the <strong>Financial</strong> category.
                  </Trans>
                </div>
              </HStack>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            mr="2"
            colorScheme="grey"
            isLoading={store.sendingInvoice}
            onClick={store.modalState.onClose}
            variant="outline"
          >
            <Trans>Close</Trans>
          </Button>
          <Button
            isDisabled={store.form.hasError}
            isLoading={store.sendingInvoice}
            onClick={store.sendEmail}
          >
            <Trans>Send</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
